import React, { useRef, useState, useEffect } from "react";
import {
  Mail,
  Phone,
  MapPin,
  Send,
  CheckCircle2,
  AlertCircle,
  Building2,
  Package,
  User,
  MessageSquare,
  ArrowUpRight,
  Loader2,
  Sparkles,
} from "lucide-react";

const BACKEND = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND}/api`;

const PRODUCTS = [
  "Tea Towels",
  "Placemats",
  "Table Runners",
  "Tablecloths",
  "Cushions",
  "Throws",
  "Curtains",
  "Kids Textiles",
  "Baby Accessories",
  "Beach Towels",
  "Outdoor Cushions",
  "Custom Development",
];

// Palette
const AUBERGINE = "#3B1930";
const AUBERGINE_SOFT = "rgba(59,25,48,0.10)";
const ROSE = "#B36478";
const ROSE_SOFT = "rgba(179,100,120,0.14)";
const CREAM = "#F5EFE4";
const CREAM_CARD = "#FBF6EC";
const TEXT_MUTED = "#5B4149";

const Reveal = ({ children, delay = 0, className = "", from = "up" }) => {
  const ref = useRef(null);
  const [visible, setVisible] = useState(false);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const obs = new IntersectionObserver(
      (entries) => {
        entries.forEach((e) => {
          if (e.isIntersecting) {
            setVisible(true);
            obs.disconnect();
          }
        });
      },
      { threshold: 0.12 }
    );
    obs.observe(el);
    return () => obs.disconnect();
  }, []);
  const initial =
    from === "left"
      ? "translateX(-30px)"
      : from === "right"
      ? "translateX(30px)"
      : "translateY(28px)";
  return (
    <div
      ref={ref}
      className={className}
      style={{
        opacity: visible ? 1 : 0,
        transform: visible ? "translate(0,0)" : initial,
        transition: `opacity 800ms ease ${delay}ms, transform 800ms cubic-bezier(0.22, 1, 0.36, 1) ${delay}ms`,
      }}
    >
      {children}
    </div>
  );
};

const FieldWrap = ({ icon: Icon, children, error }) => (
  <div className="relative">
    <div
      className="pointer-events-none absolute left-3.5 top-[14px] z-[2]"
      style={{ color: "rgba(59,25,48,0.45)" }}
    >
      <Icon className="w-4 h-4" strokeWidth={2} />
    </div>
    {children}
    {error && (
      <div className="mt-1.5 flex items-center gap-1 text-[12px] text-rose-700">
        <AlertCircle className="w-3 h-3" /> {error}
      </div>
    )}
  </div>
);

const inputCls =
  "w-full pl-10 pr-3.5 py-3 rounded-lg text-[14px] transition-all duration-200 outline-none";

const inputStyle = {
  backgroundColor: "rgba(255,251,244,0.75)",
  color: AUBERGINE,
  border: "1px solid rgba(59,25,48,0.15)",
};
const inputFocusHandler = (e) => {
  e.currentTarget.style.borderColor = AUBERGINE;
  e.currentTarget.style.boxShadow = "0 0 0 3px rgba(179,100,120,0.25)";
  e.currentTarget.style.backgroundColor = "#FFFBF4";
};
const inputBlurHandler = (e) => {
  e.currentTarget.style.borderColor = "rgba(59,25,48,0.15)";
  e.currentTarget.style.boxShadow = "none";
  e.currentTarget.style.backgroundColor = "rgba(255,251,244,0.75)";
};

const ContactSection = () => {
  const [form, setForm] = useState({
    name: "",
    email: "",
    company: "",
    phone: "",
    quantity: "",
    message: "",
  });
  const [products, setProducts] = useState([]);
  const [errors, setErrors] = useState({});
  const [status, setStatus] = useState("idle");
  const [errorMsg, setErrorMsg] = useState("");

  const setField = (k) => (e) =>
    setForm((f) => ({ ...f, [k]: e.target.value }));

  const toggleProduct = (p) =>
    setProducts((arr) =>
      arr.includes(p) ? arr.filter((x) => x !== p) : [...arr, p]
    );

  const validate = () => {
    const errs = {};
    if (!form.name.trim()) errs.name = "Please enter your name";
    if (!form.email.trim()) errs.email = "Email is required";
    else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email))
      errs.email = "Enter a valid email";
    if (!form.message.trim()) errs.message = "Please tell us how we can help";
    setErrors(errs);
    return Object.keys(errs).length === 0;
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!validate()) return;
    setStatus("loading");
    setErrorMsg("");
    try {
      const res = await fetch(`${API}/enquiries`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          name: form.name.trim(),
          email: form.email.trim(),
          company: form.company.trim() || null,
          phone: form.phone.trim() || null,
          quantity: form.quantity.trim() || null,
          product_interest: products,
          message: form.message.trim(),
        }),
      });
      if (!res.ok) {
        const txt = await res.text();
        throw new Error(txt || "Something went wrong");
      }
      setStatus("success");
      setForm({
        name: "",
        email: "",
        company: "",
        phone: "",
        quantity: "",
        message: "",
      });
      setProducts([]);
    } catch (err) {
      setStatus("error");
      setErrorMsg(err.message || "Please try again in a moment.");
    }
  };

  return (
    <section
      id="contact"
      className="relative w-full scroll-mt-20 overflow-hidden"
      style={{ backgroundColor: CREAM }}
      data-testid="contact-section"
    >
      {/* Ambient blobs (warm cream palette) */}
      <div className="absolute inset-0 pointer-events-none overflow-hidden">
        <div
          className="absolute -top-32 -left-24 w-[520px] h-[520px] rounded-full opacity-60 vm-blob"
          style={{
            background:
              "radial-gradient(circle, rgba(179,100,120,0.18) 0%, rgba(179,100,120,0) 70%)",
          }}
        />
        <div
          className="absolute bottom-0 right-0 w-[560px] h-[560px] rounded-full opacity-50 vm-blob-2"
          style={{
            background:
              "radial-gradient(circle, rgba(59,25,48,0.12) 0%, rgba(59,25,48,0) 70%)",
          }}
        />
        <svg
          className="absolute inset-0 w-full h-full opacity-[0.05]"
          viewBox="0 0 1200 800"
          preserveAspectRatio="none"
          aria-hidden="true"
        >
          <path
            d="M0,220 Q300,150 600,220 T1200,220"
            stroke={AUBERGINE}
            strokeWidth="1.2"
            fill="none"
          />
          <path
            d="M0,540 Q300,600 600,540 T1200,540"
            stroke={ROSE}
            strokeWidth="1.2"
            fill="none"
          />
          <path
            d="M0,400 Q300,340 600,400 T1200,400"
            stroke={AUBERGINE}
            strokeWidth="0.9"
            fill="none"
          />
        </svg>
      </div>

      <div className="relative max-w-[1440px] mx-auto px-6 md:px-10 py-16 md:py-24">
        {/* Section header */}
        <Reveal>
          <div className="flex flex-col items-center text-center max-w-3xl mx-auto mb-12 md:mb-16">
            <div
              className="inline-flex items-center gap-2 px-3 py-1 rounded-full text-xs font-semibold tracking-widest uppercase mb-5 contact-badge-pulse"
              style={{
                backgroundColor: ROSE_SOFT,
                color: "#7A3A52",
                border: "1px solid rgba(179,100,120,0.35)",
              }}
            >
              <Sparkles className="w-3.5 h-3.5" />
              Get in Touch
            </div>
            <h2
              className="contact-title-warm mb-3 leading-[1.15] pb-2"
              style={{
                fontFamily: "'Playfair Display', Georgia, serif",
                fontSize: "clamp(2.4rem, 5vw, 3.8rem)",
                fontWeight: 700,
                letterSpacing: "-0.02em",
              }}
            >
              Let&apos;s Weave Something Together
            </h2>
            <p
              className="text-[15px] md:text-[16px] leading-relaxed max-w-2xl mx-auto mt-4"
              style={{ color: TEXT_MUTED }}
            >
              Request samples, explore product ranges, or start a custom
              development — our team responds within one business day.
            </p>
          </div>
        </Reveal>

        <div className="grid grid-cols-1 lg:grid-cols-5 gap-6 md:gap-8">
          {/* LEFT — contact info */}
          <div className="lg:col-span-2 flex flex-col gap-4">
            <Reveal from="left" delay={100}>
              <a
                href="mailto:raghul@amber-living.in"
                className="group block relative rounded-xl p-5 md:p-6 overflow-hidden transition-all duration-300 hover:-translate-y-1"
                style={{
                  backgroundColor: CREAM_CARD,
                  border: `1px solid ${AUBERGINE_SOFT}`,
                  boxShadow: "0 12px 30px -18px rgba(59,25,48,0.15)",
                }}
                data-testid="contact-info-email"
              >
                <div className="flex items-start gap-4">
                  <div
                    className="shrink-0 w-11 h-11 rounded-full flex items-center justify-center transition-transform duration-300 group-hover:scale-110 group-hover:rotate-6"
                    style={{
                      background:
                        "linear-gradient(135deg, #3B1930 0%, #7A3A52 100%)",
                      boxShadow: "0 10px 25px -10px rgba(59,25,48,0.5)",
                    }}
                  >
                    <Mail className="w-5 h-5 text-white" strokeWidth={2.2} />
                  </div>
                  <div className="min-w-0 flex-1">
                    <div
                      className="text-[11px] tracking-[0.24em] uppercase mb-1"
                      style={{ color: "#7A3A52" }}
                    >
                      Email Us
                    </div>
                    <div
                      className="font-semibold text-[15px] md:text-[16px] break-all transition-colors"
                      style={{ color: AUBERGINE }}
                    >
                      raghul@amber-living.in
                    </div>
                    <div
                      className="text-[12.5px] mt-1"
                      style={{ color: TEXT_MUTED }}
                    >
                      We reply within one business day
                    </div>
                  </div>
                  <ArrowUpRight
                    className="w-4 h-4 group-hover:rotate-45 transition-all duration-300 shrink-0"
                    style={{ color: ROSE }}
                    strokeWidth={2}
                  />
                </div>
              </a>
            </Reveal>

            <Reveal from="left" delay={200}>
              <a
                href="tel:+919585956304"
                className="group block relative rounded-xl p-5 md:p-6 overflow-hidden transition-all duration-300 hover:-translate-y-1"
                style={{
                  backgroundColor: CREAM_CARD,
                  border: `1px solid ${AUBERGINE_SOFT}`,
                  boxShadow: "0 12px 30px -18px rgba(59,25,48,0.15)",
                }}
                data-testid="contact-info-phone"
              >
                <div className="flex items-start gap-4">
                  <div
                    className="shrink-0 w-11 h-11 rounded-full flex items-center justify-center transition-transform duration-300 group-hover:scale-110 group-hover:-rotate-6"
                    style={{
                      background:
                        "linear-gradient(135deg, #3B1930 0%, #7A3A52 100%)",
                      boxShadow: "0 10px 25px -10px rgba(59,25,48,0.5)",
                    }}
                  >
                    <Phone className="w-5 h-5 text-white" strokeWidth={2.2} />
                  </div>
                  <div className="min-w-0 flex-1">
                    <div
                      className="text-[11px] tracking-[0.24em] uppercase mb-1"
                      style={{ color: "#7A3A52" }}
                    >
                      Call / WhatsApp
                    </div>
                    <div
                      className="font-semibold text-[15px] md:text-[16px]"
                      style={{ color: AUBERGINE }}
                    >
                      +91 95859 56304
                    </div>
                    <div
                      className="text-[12.5px] mt-1"
                      style={{ color: TEXT_MUTED }}
                    >
                      Mon–Sat · 9:30 AM – 7:00 PM IST
                    </div>
                  </div>
                  <ArrowUpRight
                    className="w-4 h-4 group-hover:rotate-45 transition-all duration-300 shrink-0"
                    style={{ color: ROSE }}
                    strokeWidth={2}
                  />
                </div>
              </a>
            </Reveal>

            <Reveal from="left" delay={300}>
              <a
                href="https://www.google.com/maps/search/?api=1&query=Plot+No.C-28+Puthambur+Village+Karur+Textile+Park+Karur+Tamil+Nadu+639003"
                target="_blank"
                rel="noopener noreferrer"
                className="group block relative rounded-xl p-5 md:p-6 overflow-hidden transition-all duration-300 hover:-translate-y-1"
                style={{
                  backgroundColor: CREAM_CARD,
                  border: `1px solid ${AUBERGINE_SOFT}`,
                  boxShadow: "0 12px 30px -18px rgba(59,25,48,0.15)",
                }}
                data-testid="contact-info-address"
              >
                <div className="flex items-start gap-4">
                  <div
                    className="shrink-0 w-11 h-11 rounded-full flex items-center justify-center transition-transform duration-300 group-hover:scale-110"
                    style={{
                      background:
                        "linear-gradient(135deg, #3B1930 0%, #7A3A52 100%)",
                      boxShadow: "0 10px 25px -10px rgba(59,25,48,0.5)",
                    }}
                  >
                    <MapPin className="w-5 h-5 text-white" strokeWidth={2.2} />
                  </div>
                  <div className="min-w-0 flex-1">
                    <div
                      className="text-[11px] tracking-[0.24em] uppercase mb-1"
                      style={{ color: "#7A3A52" }}
                    >
                      Visit Our Factory
                    </div>
                    <div
                      className="font-semibold text-[14.5px] leading-snug"
                      style={{ color: AUBERGINE }}
                    >
                      Plot No. C-28, Puthambur Village
                      <br />
                      Karur Textile Park
                      <br />
                      Karur, Tamil Nadu, India — 639003
                    </div>
                  </div>
                </div>
              </a>
            </Reveal>
          </div>

          {/* RIGHT — form */}
          <Reveal from="right" delay={150} className="lg:col-span-3">
            <form
              onSubmit={submit}
              noValidate
              data-testid="contact-form"
              className="relative rounded-2xl p-6 md:p-8 overflow-hidden"
              style={{
                backgroundColor: CREAM_CARD,
                border: `1px solid ${AUBERGINE_SOFT}`,
                boxShadow:
                  "0 40px 80px -30px rgba(59,25,48,0.18), inset 0 1px 0 rgba(255,255,255,0.6)",
              }}
            >
              <div
                className="pointer-events-none absolute -top-24 -right-24 w-64 h-64 rounded-full opacity-50"
                style={{
                  background:
                    "radial-gradient(circle, rgba(179,100,120,0.25) 0%, rgba(179,100,120,0) 70%)",
                }}
              />

              {/* Illuminated + wiggling ENQUIRY FORM title */}
              <div className="relative flex justify-center mb-6">
                <div
                  className="enquiry-title-glow inline-flex items-center gap-2 px-4 py-1.5 rounded-full text-[11px] font-bold tracking-[0.32em] uppercase"
                  style={{
                    backgroundColor: "rgba(179,100,120,0.14)",
                    color: "#7A3A52",
                    border: "1px solid rgba(220,60,80,0.55)",
                  }}
                  data-testid="enquiry-form-title"
                >
                  <Sparkles className="w-3.5 h-3.5 icon-wiggle" />
                  Enquiry Form
                </div>
              </div>

              {status === "success" ? (
                <div
                  className="relative flex flex-col items-center text-center py-10"
                  data-testid="contact-success"
                >
                  <div
                    className="w-16 h-16 rounded-full flex items-center justify-center mb-5 contact-success-pop"
                    style={{
                      background:
                        "linear-gradient(135deg, #6B8E4E 0%, #4E7A3A 100%)",
                      boxShadow: "0 15px 40px -12px rgba(78,122,58,0.55)",
                    }}
                  >
                    <CheckCircle2
                      className="w-8 h-8 text-white"
                      strokeWidth={2.5}
                    />
                  </div>
                  <h3
                    className="mb-2"
                    style={{
                      color: AUBERGINE,
                      fontFamily: "'Playfair Display', Georgia, serif",
                      fontSize: "clamp(1.4rem, 2.4vw, 1.8rem)",
                      fontWeight: 600,
                    }}
                  >
                    Enquiry Received — Thank You!
                  </h3>
                  <p
                    className="text-[14.5px] max-w-md leading-relaxed mb-6"
                    style={{ color: TEXT_MUTED }}
                  >
                    Our team will get back to you at{" "}
                    <span
                      style={{ color: ROSE, fontWeight: 600 }}
                    >
                      raghul@amber-living.in
                    </span>{" "}
                    within one business day.
                  </p>
                  <button
                    type="button"
                    onClick={() => setStatus("idle")}
                    className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full text-[13px] font-semibold text-white transition-transform duration-200 hover:-translate-y-0.5"
                    style={{
                      background:
                        "linear-gradient(135deg, #3B1930 0%, #7A3A52 100%)",
                    }}
                    data-testid="contact-new-enquiry-btn"
                  >
                    Send another enquiry
                  </button>
                </div>
              ) : (
                <div className="relative">
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 md:gap-5">
                    <FieldWrap icon={User} error={errors.name}>
                      <input
                        type="text"
                        placeholder="Your name *"
                        value={form.name}
                        onChange={setField("name")}
                        className={inputCls}
                        style={inputStyle}
                        onFocus={inputFocusHandler}
                        onBlur={inputBlurHandler}
                        data-testid="contact-name-input"
                      />
                    </FieldWrap>
                    <FieldWrap icon={Mail} error={errors.email}>
                      <input
                        type="email"
                        placeholder="Email address *"
                        value={form.email}
                        onChange={setField("email")}
                        className={inputCls}
                        style={inputStyle}
                        onFocus={inputFocusHandler}
                        onBlur={inputBlurHandler}
                        data-testid="contact-email-input"
                      />
                    </FieldWrap>
                    <FieldWrap icon={Building2}>
                      <input
                        type="text"
                        placeholder="Company"
                        value={form.company}
                        onChange={setField("company")}
                        className={inputCls}
                        style={inputStyle}
                        onFocus={inputFocusHandler}
                        onBlur={inputBlurHandler}
                        data-testid="contact-company-input"
                      />
                    </FieldWrap>
                    <FieldWrap icon={Phone}>
                      <input
                        type="tel"
                        placeholder="Phone (optional)"
                        value={form.phone}
                        onChange={setField("phone")}
                        className={inputCls}
                        style={inputStyle}
                        onFocus={inputFocusHandler}
                        onBlur={inputBlurHandler}
                        data-testid="contact-phone-input"
                      />
                    </FieldWrap>
                  </div>

                  {/* Product chips */}
                  <div className="mt-5">
                    <div
                      className="text-[12px] font-semibold uppercase tracking-widest mb-3 flex items-center gap-2"
                      style={{ color: "#7A3A52" }}
                    >
                      <Package className="w-3.5 h-3.5" /> Product Interest
                      <span
                        className="font-normal normal-case tracking-normal text-[11.5px]"
                        style={{ color: "rgba(59,25,48,0.5)" }}
                      >
                        (choose any)
                      </span>
                    </div>
                    <div
                      className="flex flex-wrap gap-2"
                      data-testid="contact-product-chips"
                    >
                      {PRODUCTS.map((p) => {
                        const active = products.includes(p);
                        return (
                          <button
                            type="button"
                            key={p}
                            onClick={() => toggleProduct(p)}
                            data-testid={`product-chip-${p
                              .toLowerCase()
                              .replace(/[^a-z0-9]+/g, "-")}`}
                            className="px-3.5 py-1.5 rounded-full text-[12.5px] font-medium transition-all duration-200"
                            style={{
                              backgroundColor: active ? AUBERGINE : "transparent",
                              color: active ? "#FBE9DA" : AUBERGINE,
                              border: active
                                ? `1px solid ${AUBERGINE}`
                                : "1px solid rgba(59,25,48,0.2)",
                              boxShadow: active
                                ? "0 6px 18px -8px rgba(59,25,48,0.5)"
                                : "none",
                            }}
                          >
                            {p}
                          </button>
                        );
                      })}
                    </div>
                  </div>

                  <div className="mt-5">
                    <FieldWrap icon={Package}>
                      <input
                        type="text"
                        placeholder="Estimated quantity (e.g. 5,000 pcs / month)"
                        value={form.quantity}
                        onChange={setField("quantity")}
                        className={inputCls}
                        style={inputStyle}
                        onFocus={inputFocusHandler}
                        onBlur={inputBlurHandler}
                        data-testid="contact-quantity-input"
                      />
                    </FieldWrap>
                  </div>

                  <div className="mt-5">
                    <FieldWrap icon={MessageSquare} error={errors.message}>
                      <textarea
                        rows={5}
                        placeholder="Tell us about your project, target markets, and any specific requirements. *"
                        value={form.message}
                        onChange={setField("message")}
                        className={`${inputCls} resize-none py-3.5`}
                        style={inputStyle}
                        onFocus={inputFocusHandler}
                        onBlur={inputBlurHandler}
                        data-testid="contact-message-input"
                      />
                    </FieldWrap>
                  </div>

                  {status === "error" && (
                    <div
                      className="mt-4 flex items-start gap-2 rounded-lg px-3.5 py-3 text-[13px]"
                      style={{
                        backgroundColor: "rgba(220,38,38,0.08)",
                        border: "1px solid rgba(220,38,38,0.25)",
                        color: "#991B1B",
                      }}
                      data-testid="contact-error"
                    >
                      <AlertCircle
                        className="w-4 h-4 mt-0.5 shrink-0"
                        strokeWidth={2}
                      />
                      <span>
                        {errorMsg ||
                          "We couldn't send your enquiry. Please try again."}
                      </span>
                    </div>
                  )}

                  <div className="mt-6 flex flex-col sm:flex-row sm:items-center gap-3 sm:justify-between">
                    <p
                      className="text-[12px] leading-relaxed max-w-sm"
                      style={{ color: "rgba(59,25,48,0.55)" }}
                    >
                      By submitting, you agree we may contact you about your
                      enquiry. We never share your details.
                    </p>
                    <button
                      type="submit"
                      disabled={status === "loading"}
                      className="group relative inline-flex items-center justify-center gap-2 px-6 py-3 rounded-full text-[13.5px] font-semibold text-white overflow-hidden transition-all duration-300 hover:-translate-y-0.5 disabled:opacity-70 disabled:cursor-not-allowed disabled:translate-y-0"
                      style={{
                        background:
                          "linear-gradient(135deg, #3B1930 0%, #7A3A52 100%)",
                        boxShadow: "0 12px 28px -10px rgba(59,25,48,0.55)",
                        minWidth: "180px",
                      }}
                      data-testid="contact-submit-btn"
                    >
                      {status === "loading" ? (
                        <>
                          <Loader2 className="w-4 h-4 animate-spin" />
                          Sending…
                        </>
                      ) : (
                        <>
                          <span className="relative z-10">Send Enquiry</span>
                          <Send
                            className="w-4 h-4 relative z-10 transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5"
                            strokeWidth={2.2}
                          />
                          <span
                            className="absolute inset-y-0 -left-1/2 w-1/3 skew-x-[-20deg] bg-white/25 opacity-0 group-hover:opacity-100 group-hover:left-full transition-all duration-700"
                            aria-hidden="true"
                          />
                        </>
                      )}
                    </button>
                  </div>
                </div>
              )}
            </form>
          </Reveal>
        </div>
      </div>
    </section>
  );
};

export default ContactSection;
